// engine-exception: the ticket workspace is a conversation (public/internal feed + outcome-driven composer with time) beside a per-type inspector and the working-set rail; the generic detail screen has no feed/composer/queue
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAction, useApiPages, useApiQuery, useInvalidateResource, useMeta, useResource, useUpdate } from '../../engine/api/hooks';
import { useEngine } from '../../engine/api/context';
import { useHotkeys } from '../../engine/layout/shortcuts';
import { useToast } from '../../engine/design/primitives/Toast';
import { AutoForm } from '../../engine/forms/AutoForm';
import { Badge, EmptyState, ErrorBanner, PageHeader, Skeleton, Chip } from '../../engine/design/primitives/Misc';
import { Button, IconButton } from '../../engine/design/primitives/Button';
import { Field, Input, Select, Switch } from '../../engine/design/primitives/Input';
import { Combobox } from '../../engine/design/primitives/Combobox';
import { Dialog } from '../../engine/design/primitives/Overlay';
import { Tabs } from '../../engine/design/primitives/Tabs';
import { Icon, type IconName } from '../../engine/design/icons';
import { RichTextEditor, RichTextView } from '../../engine/fields/RichText';
import { DurationEditor } from '../../engine/fields/registry';
import { ActivityFeed } from '../../engine/views/ActivityFeed';
import { AttachmentsPanel } from '../../engine/views/AttachmentsPanel';
import { SplitPane } from '../../engine/views/SplitPane';
import { formatDateTime, formatMinutes } from '../../lib/format';
import { priorityTone, statusTone, useOutcomes, usePriorities, useQueues, useStatuses, useTechnicians, useTicketTypes, type OutcomeRow, type StatusRow, fieldConfigOf, orderTicketFields } from './lib/catalog';
import { applyFieldConfig } from './NewTicketForm';
import { clockSummary, formatRemaining, slaTone, type SlaClock } from './lib/sla';
import { QueueRail } from './QueueRail';
import { TimerControl } from './TimerControl';
import { CommercialsPanel } from './CommercialsPanel';
import { SchedulePanel } from '../scheduling';
import { AssistPanel } from '../ai';
import { useWorkingSet, WORKING_SET_STORAGE_KEY, type WorkingSetMode } from './lib/workspace';
import { useNextTicket } from './DispatchScreen';

/** Focus a control by id — the keyboard map's targets are the composer's own fields. */
/** Block fields are long text; with two or more they move to a "More info" tab (one stays inline). */
export function splitBlockFields<F extends { type: string }>(fields: F[]): { detailFields: F[]; moreFields: F[] } {
  const block = fields.filter((f) => f.type === 'textarea' || f.type === 'richtext');
  if (block.length < 2) return { detailFields: fields, moreFields: [] };
  return { detailFields: fields.filter((f) => !block.includes(f)), moreFields: block };
}

function focusById(id: string) {
  const el = document.getElementById(id);
  if (el instanceof HTMLElement) el.focus();
}

function readMode(): WorkingSetMode {
  try {
    const v = localStorage.getItem(WORKING_SET_STORAGE_KEY);
    if (v === 'mine' || v === 'queue' || v === 'all') return v;
  } catch {
    /* ignore */
  }
  return 'mine';
}

// The ticket workspace, built from engine parts: PageHeader + chips (type, priority, SLA
// countdowns coloured by % consumed), a status Select driven by the status catalog, an
// assignee Combobox, watch/escalate/reopen/merge actions; the conversation feed (public /
// internal / all; email, note, call, system entries) with a composer whose OUTCOME decides the
// effects (status after, public, email, time via h:mm → one time_entry); and a right pane
// (AutoForm over the type's field_config, contact/site/client cards, links, checklist,
// approvals, SLA clocks, activity). Every mutation is a resource action on /v1/tickets.

type Row = Record<string, unknown>;
interface TimelineEntry extends Row { id: string; kind: string; body: string | null; body_text: string | null; is_public: boolean; is_response: boolean; author_name: string | null; technician_name: string | null; outcome_name: string | null; status_after_name: string | null; assignee_after_name: string | null; minutes: number | null; diff: Record<string, unknown>; created_at: string; actor_kind: string; edited_at: string | null }

const KIND_ICON: Record<string, IconName> = { note: 'sticky-note', email_in: 'mail', email_out: 'send', call: 'phone', status_change: 'circle-dot', assignment: 'user', time: 'timer', system: 'zap', portal: 'globe', approval: 'shield-check' };

/** Feed page size: the newest entries first, older ones on demand. */
export const FEED_PAGE = 20;

/** The conversation, newest first, a page at a time; "public" is filtered by the API, "internal" here. */
function useTimeline(id: string, filter: 'all' | 'public' | 'internal') {
  return useApiPages<TimelineEntry>(['ticket-timeline', id, filter === 'public' ? 'public' : 'all'], `/v1/tickets/${id}/timeline`, {
    enabled: Boolean(id),
    query: { limit: FEED_PAGE, public: filter === 'public' ? 1 : undefined },
  });
}

/**
 * SLA chips from the ticket's own due dates when it has no SLA clocks — a ticket served by a
 * connected system carries its respond-by / fix-by there (Halo `respondbydate` / `fixbydate`).
 */
export function dueChips(ticket: Row, now = Date.now()): { key: string; label: string; tone: 'pos' | 'warn' | 'neg' | 'muted'; title: string }[] {
  const out: { key: string; label: string; tone: 'pos' | 'warn' | 'neg' | 'muted'; title: string }[] = [];
  const add = (key: string, name: string, at: unknown, done: unknown) => {
    if (typeof at !== 'string' || done) return;
    const mins = (new Date(at).getTime() - now) / 60_000;
    if (!Number.isFinite(mins)) return;
    out.push({ key, label: `${name} ${formatRemaining(mins)}`, tone: mins < 0 ? 'neg' : mins < 60 ? 'warn' : 'pos', title: `${name} by ${formatDateTime(at)}` });
  };
  add('response', 'Respond', ticket['first_response_due_at'], ticket['responded_at']);
  add('resolution', 'Fix', ticket['resolution_due_at'], ticket['resolved_at'] ?? ticket['closed_at']);
  return out;
}

export function SlaChips({ clocks }: { clocks: SlaClock[] }) {
  const latest = useMemo(() => {
    const m = new Map<string, SlaClock>();
    for (const c of clocks) if (!m.has(c.kind) || m.get(c.kind)!.cycle < c.cycle) m.set(c.kind, c);
    return [...m.values()];
  }, [clocks]);
  if (latest.length === 0) return null;
  return (
    <>
      {latest.map((c) => (
        <Badge key={c.id} tone={slaTone(c)} title={`Target ${formatDateTime(c.target_at)} · ${c.elapsed_pct ?? 0}% elapsed`}>
          {clockSummary(c)}
        </Badge>
      ))}
    </>
  );
}

/** Who will see what the composer sends — said before sending, not discovered after. */
export function visibilitySentence(isPublic: boolean, email: boolean): string {
  if (!isPublic) return 'Internal — only your team sees this.';
  return email ? 'Public — the customer sees this and is emailed a copy.' : 'Public — the customer sees this in the portal; no email is sent.';
}

/** The send button says what it does. */
export function sendLabel(isPublic: boolean, email: boolean): string {
  if (!isPublic) return 'Add internal note';
  return email ? 'Send to customer' : 'Post public reply';
}

/**
 * One submit does everything (proposal P1): outcome + note + time + visibility + email + an
 * optional status override go to POST /v1/tickets/:id/actions in a single request, so a reply,
 * its time entry and the status change are one keystroke (⌘↵) and one append-only action —
 * never four saves. ⌘⇧↵ additionally advances to the next ticket in the working set.
 */
function Composer({ ticketId, outcomes, statuses, onDone }: { ticketId: string; outcomes: OutcomeRow[]; statuses: StatusRow[]; onDone: (opts: { next: boolean }) => void }) {
  const [outcomeId, setOutcomeId] = useState<string>(() => outcomes.find((o) => o.key === 'note')?.id ?? outcomes[0]?.id ?? '');
  const outcome = outcomes.find((o) => o.id === outcomeId) ?? null;
  const [body, setBody] = useState('');
  const [minutes, setMinutes] = useState<number | null>(null);
  const [isPublic, setIsPublic] = useState<boolean | null>(null);
  const [sendEmail, setSendEmail] = useState<boolean | null>(null);
  const [statusId, setStatusId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const act = useAction('tickets', 'actions', { toast: { success: 'Action added' } });
  const effectivePublic = isPublic ?? outcome?.is_public ?? false;
  const effectiveEmail = sendEmail ?? outcome?.sends_email ?? false;

  const submit = useCallback(
    async (opts: { next: boolean }) => {
      setError(null);
      if (outcome?.requires_note && body.replace(/<[^>]+>/g, '').trim() === '') return setError(`"${outcome.name}" needs a note.`);
      if (outcome?.requires_time && !(minutes && minutes > 0)) return setError(`"${outcome.name}" needs time.`);
      try {
        await act.mutateAsync({
          id: ticketId,
          body: { outcome_id: outcomeId || undefined, body: body || undefined, is_public: effectivePublic, send_email: effectiveEmail, minutes: minutes ?? undefined, status_id: statusId || undefined },
        });
        setBody('');
        setMinutes(null);
        setIsPublic(null);
        setSendEmail(null);
        setStatusId('');
        onDone(opts);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not add the action');
      }
    },
    [act, body, effectiveEmail, effectivePublic, minutes, onDone, outcome, outcomeId, statusId, ticketId],
  );

  // Global so they fire from inside the editor — the composer is where the hands already are.
  useHotkeys([
    { keys: 'mod+enter', label: 'Send the composer', group: 'Ticket', global: true, run: () => void submit({ next: false }) },
    { keys: 'mod+shift+enter', label: 'Send and open the next ticket', group: 'Ticket', global: true, run: () => void submit({ next: true }) },
  ]);

  return (
    <section className="hf-card pad hf-stack" aria-label="Reply">
      <div className="hf-row" style={{ flexWrap: 'wrap' }}>
        <Field label="Outcome" id="sd-outcome">
          <Select id="sd-outcome" size="sm" value={outcomeId} onChange={(e) => { setOutcomeId(e.target.value); setIsPublic(null); setSendEmail(null); }} options={outcomes.filter((o) => o.is_active).map((o) => ({ value: o.id, label: o.button_label ?? o.name }))} />
        </Field>
        <Field label="Time" id="sd-minutes">
          <DurationEditor id="sd-minutes" size="sm" field={{ name: 'minutes', label: 'Time', type: 'duration', required: false, readOnly: false, nullable: true, filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: false, inList: false, inDetail: false }} value={minutes} onChange={(v) => setMinutes(typeof v === 'number' ? v : null)} aria-label="Time taken (h:mm)" />
        </Field>
        <Field label="Then set status" id="sd-composer-status">
          <Select id="sd-composer-status" size="sm" value={statusId} onChange={(e) => setStatusId(e.target.value)}
            options={[{ value: '', label: outcome?.status_after_id ? 'From the outcome' : 'Leave as is' }, ...statuses.filter((s) => s.is_active).map((s) => ({ value: s.id, label: s.name }))]} />
        </Field>
        <Switch label="Public" checked={effectivePublic} onChange={(e) => setIsPublic(e.target.checked)} />
        <Switch label="Email customer" checked={effectiveEmail} disabled={!effectivePublic} onChange={(e) => setSendEmail(e.target.checked)} />
        {outcome?.status_after_id && !statusId && (
          <span className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>
            <Icon name="arrow-right" size={12} /> sets status
          </span>
        )}
      </div>
      <p className={['hf-composer-visibility', effectivePublic ? 'public' : 'internal'].join(' ')} role="status">
        <Icon name={effectivePublic ? 'globe' : 'lock'} size={12} /> {visibilitySentence(effectivePublic, effectiveEmail)}
      </p>
      <RichTextEditor id="sd-composer-body" value={body} onChange={setBody} placeholder={effectivePublic ? 'Reply to the customer…' : 'Internal note…'} aria-label="Action body" />
      {error && <div className="hf-error" role="alert">{error}</div>}
      <div className="hf-between">
        <AttachmentsPanel entityType="ticket" entityId={ticketId} />
        <div className="hf-row">
          <Button variant="ghost" icon="arrow-right" busy={act.isPending} onClick={() => void submit({ next: true })} title="Send and open the next ticket (⌘⇧↵)">
            Send &amp; next
          </Button>
          <Button variant="primary" icon="send" busy={act.isPending} onClick={() => void submit({ next: false })} title="Send (⌘↵)">
            {sendLabel(effectivePublic, effectiveEmail)}
          </Button>
        </div>
      </div>
    </section>
  );
}

/** An entry long enough to collapse (characters of text, or lines). */
export const LONG_ENTRY_CHARS = 700;
const isLong = (e: TimelineEntry) => (e.body_text ?? '').length > LONG_ENTRY_CHARS || (e.body_text ?? '').split('\n').length > 14;

function FeedBody({ entry }: { entry: TimelineEntry }) {
  const [open, setOpen] = useState(false);
  if (!entry.body) return null;
  if (!isLong(entry)) return <RichTextView html={entry.body} />;
  return (
    <div className={['hf-clamp', open ? 'open' : ''].join(' ')}>
      <div className="hf-clamp-body"><RichTextView html={entry.body} /></div>
      <Button size="sm" variant="link" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{open ? 'Show less' : 'Show more'}</Button>
    </div>
  );
}

/** The conversation, newest first; `entries` arrive newest first from the API. */
function Feed({ entries, filter, hasMore, loadingMore, onMore }: { entries: TimelineEntry[]; filter: 'all' | 'public' | 'internal'; hasMore: boolean; loadingMore: boolean; onMore: () => void }) {
  const shown = entries.filter((e) => (filter === 'internal' ? !e.is_public : true));
  if (shown.length === 0 && !hasMore) return <EmptyState title="Nothing here yet" icon="message-square" />;
  return (
    <>
      <ol className="hf-feed" aria-label="Conversation">
        {shown.map((e) => (
          <li key={e.id} className={['hf-feed-item', e.is_public ? 'public' : 'internal'].join(' ')}>
            <div className="hf-row" style={{ fontSize: 'var(--t-12)' }}>
              <Icon name={KIND_ICON[e.kind] ?? 'circle'} size={14} label={e.kind} />
              <strong>{e.author_name ?? e.technician_name ?? (e.actor_kind === 'system' ? 'System' : 'Customer')}</strong>
              {e.outcome_name && <Badge tone="muted">{e.outcome_name}</Badge>}
              <Badge tone={e.is_public ? 'accent' : 'muted'} dot={false}>{e.is_public ? 'Public' : 'Internal'}</Badge>
              {e.is_response && <Badge tone="pos" dot={false}>Response</Badge>}
              {e.minutes != null && e.minutes > 0 && <span className="hf-num hf-muted">{formatMinutes(e.minutes)}</span>}
              <span className="hf-grow" />
              <time className="hf-muted hf-num" dateTime={e.created_at}>{formatDateTime(e.created_at)}</time>
            </div>
            <FeedBody entry={e} />
            {e.status_after_name && <div className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>→ {e.status_after_name}</div>}
            {e.assignee_after_name && e.kind === 'assignment' && <div className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>→ {e.assignee_after_name}</div>}
            {e.edited_at && <div className="hf-muted" style={{ fontSize: 'var(--t-11)' }}>edited</div>}
          </li>
        ))}
      </ol>
      {hasMore && (
        <Button variant="ghost" icon="chevron-down" busy={loadingMore} onClick={onMore}>
          Show older
        </Button>
      )}
    </>
  );
}

/** The ticket's description: long ones are clamped with a fade until opened. */
function Description({ html }: { html: string }) {
  const [open, setOpen] = useState(false);
  const long = html.replace(/<[^>]+>/g, '').length > LONG_ENTRY_CHARS;
  return (
    <section className={['hf-card pad', long ? 'hf-clamp' : '', open ? 'open' : ''].join(' ')} aria-label="Description">
      <div className={long ? 'hf-clamp-body' : undefined}><RichTextView html={html} /></div>
      {long && <Button size="sm" variant="link" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{open ? 'Show less' : 'Show more'}</Button>}
    </section>
  );
}

function Card({ title, children, icon }: { title: string; children: ReactNode; icon?: IconName }) {
  return (
    <section className="hf-stack" style={{ gap: 'var(--s-2)' }} aria-label={title}>
      <div className="hf-caps hf-row">{icon && <Icon name={icon} size={12} />}{title}</div>
      {children}
    </section>
  );
}

function RelatedCard({ resource, id, fields, title, icon }: { resource: string; id: string | null; fields: string[]; title: string; icon: IconName }) {
  const q = useResource<Row>(resource, id, { enabled: Boolean(id) });
  if (!id) return <Card title={title} icon={icon}><span className="hf-muted">—</span></Card>;
  if (q.isLoading) return <Card title={title} icon={icon}><Skeleton lines={2} /></Card>;
  const row = q.data ?? {};
  return (
    <Card title={title} icon={icon}>
      <dl className="hf-kv">
        {fields.filter((f) => row[f] != null && row[f] !== '').map((f) => (
          <div key={f}><dt>{f.replace(/_/g, ' ')}</dt><dd>{String(row[f])}</dd></div>
        ))}
      </dl>
    </Card>
  );
}

/** The mailbox reading pane: the ticket workspace for the open ticket. */
export function TicketReading({ id }: { id: string }) {
  return <TicketDetailScreen id={id} embedded />;
}

/** The workspace for the routed ticket, or for `id` when embedded (the mailbox reading pane). */
export function TicketDetailScreen({ id: idProp, embedded = false }: { id?: string; embedded?: boolean } = {}) {
  const params = useParams();
  const id = idProp ?? params.id ?? '';
  const navigate = useNavigate();
  const { principal } = useEngine();
  const meta = useMeta('tickets');
  const ticketQ = useResource<Row>('tickets', id, { expand: ['type', 'status', 'priority', 'impact', 'urgency', 'queue', 'sla_policy', 'client', 'contact', 'site', 'assigned_tech', 'contract', 'category'] });
  const ticket = ticketQ.data;
  const sla = useApiQuery<{ data: SlaClock[] }>(['ticket-sla', id], `/v1/tickets/${id}/sla`, { enabled: Boolean(id) });
  const links = useApiQuery<{ data: Row[] }>(['ticket-links', id], `/v1/tickets/${id}/links`, { enabled: Boolean(id) });
  const checklist = useApiQuery<{ data: Row[] }>(['ticket-checklist', id], `/v1/tickets/${id}/checklist`, { enabled: Boolean(id) });
  const approvals = useApiQuery<{ data: Row[] }>(['ticket-approvals', id], `/v1/tickets/${id}/approvals`, { enabled: Boolean(id) });
  const statuses = useStatuses();
  const priorities = usePriorities();
  const queues = useQueues();
  const techs = useTechnicians();
  const types = useTicketTypes();
  const outcomes = useOutcomes();
  const invalidate = useInvalidateResource();
  const toast = useToast();
  const update = useUpdate('tickets', { toast: { success: 'Saved' } });
  // Status and assignment toast themselves so the toast can carry Undo (the inverse call).
  const status = useAction('tickets', 'status', { toast: { success: false } });
  const assign = useAction('tickets', 'assign', { toast: { success: false } });
  const watch = useAction('tickets', 'watch');
  const escalate = useAction('tickets', 'escalate', { toast: { success: 'Escalated' } });
  const reopen = useAction('tickets', 'reopen', { toast: { success: 'Reopened' } });
  const merge = useAction('tickets', 'merge', { toast: { success: 'Merged' } });
  const toggle = useAction('tickets', 'checklist-toggle');
  const vote = useAction('approvals', 'vote', { toast: { success: 'Vote recorded' } });
  const [feedFilter, setFeedFilter] = useState<'all' | 'public' | 'internal'>('all');
  const timeline = useTimeline(id, feedFilter);
  const [tab, setTab] = useState('details');
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeInto, setMergeInto] = useState('');
  const [inspectorOpen, setInspectorOpen] = useState(true);
  // The working-set rail is a third pane: below ~1440px it leaves the composer a sliver, so it
  // starts folded there (`r` / the header toggle bring it back). Wide screens keep all three.
  const [railOpen, setRailOpen] = useState(() => !embedded && !(typeof window !== 'undefined' && window.matchMedia?.('(max-width: 1439px)').matches));
  const [mode, setMode] = useState<WorkingSetMode>(readMode);

  // The working set: the rail, `j`/`k`, and "send and next" all read from it.
  const workingSet = useWorkingSet(mode, { queueId: (ticket?.['queue_id'] as string | null) ?? null });
  const nextId = workingSet.nextId(id);
  const prevId = workingSet.prevId(id);
  // Prefetch the next ticket so "send and next" lands on a rendered screen, not a spinner.
  useResource<Row>('tickets', nextId, { enabled: Boolean(nextId) });

  const refreshAll = useCallback(() => {
    invalidate('tickets', id);
    timeline.refetch();
    void sla.refetch();
    void links.refetch();
    void checklist.refetch();
    void approvals.refetch();
    workingSet.refetch();
    // The queries are stable per id; refreshing on every render-identity change would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, invalidate]);

  const openTicket = useCallback((target: string | null) => { if (target) navigate(`/tickets/${target}`); }, [navigate]);

  const changeMode = useCallback((m: WorkingSetMode) => {
    setMode(m);
    try {
      localStorage.setItem(WORKING_SET_STORAGE_KEY, m);
    } catch {
      /* ignore */
    }
  }, []);

  /** Status/assignment through the actions, with the inverse offered as Undo for 10 seconds. */
  const changeStatus = useCallback(
    (toStatusId: string) => {
      const previous = (ticket?.['status_id'] as string | null) ?? null;
      status.mutate(
        { id, body: { status_id: toStatusId } },
        {
          onSuccess: () => {
            refreshAll();
            toast({
              title: 'Status changed',
              action: previous ? { label: 'Undo', run: async () => { await status.mutateAsync({ id, body: { status_id: previous } }); refreshAll(); } } : undefined,
            });
          },
        },
      );
    },
    [id, refreshAll, status, ticket, toast],
  );

  const changeAssignee = useCallback(
    (technicianId: string | null) => {
      const previous = (ticket?.['assigned_tech_id'] as string | null) ?? null;
      assign.mutate(
        { id, body: { technician_id: technicianId } },
        {
          onSuccess: () => {
            refreshAll();
            toast({
              title: technicianId ? 'Assigned' : 'Unassigned',
              action: { label: 'Undo', run: async () => { await assign.mutateAsync({ id, body: { technician_id: previous } }); refreshAll(); } },
            });
          },
        },
      );
    },
    [assign, id, refreshAll, ticket, toast],
  );

  // `n` asks dispatch for the next ticket rather than the next one in the rail: when a technician
  // finishes something, the right question is "what now", not "what is below this".
  const dispatched = useNextTicket();

  // The keyboard map (proposal P1). Nothing here needs the mouse; `?` lists them all.
  useHotkeys([
    { keys: 'j', label: 'Next ticket in the working set', group: 'Ticket', run: () => openTicket(nextId) },
    { keys: 'k', label: 'Previous ticket in the working set', group: 'Ticket', run: () => openTicket(prevId) },
    { keys: 'e', label: 'Write a reply or note', group: 'Ticket', run: () => focusById('sd-composer-body') },
    { keys: 't', label: 'Log time', group: 'Ticket', run: () => focusById('sd-minutes') },
    { keys: 's', label: 'Change status', group: 'Ticket', run: () => focusById('sd-status') },
    { keys: 'a', label: 'Assign', group: 'Ticket', run: () => focusById('sd-assignee') },
    { keys: 'i', label: 'Show or hide the inspector', group: 'Ticket', run: () => setInspectorOpen((v) => !v) },
    { keys: 'r', label: 'Show or hide the working-set rail', group: 'Ticket', run: () => setRailOpen((v) => !v) },
    {
      keys: 'n', label: 'Ask dispatch what to work on next', group: 'Ticket',
      run: () => {
        void dispatched.refetch().then((r) => {
          const target = (r.data?.ticket as Row | null | undefined)?.['id'];
          navigate(typeof target === 'string' ? `/tickets/${target}` : '/dispatch');
        });
      },
    },
  ]);

  const type = types.byId.get(String(ticket?.['type_id']));
  const st = statuses.byId.get(String(ticket?.['status_id']));
  const pr = priorities.byId.get(String(ticket?.['priority_id']));
  const queue = queues.byId.get(String(ticket?.['queue_id']));
  const allDetailFields = useMemo(() => {
    if (!meta.data) return [];
    const base = meta.data.fields.filter((f) => f.inUpdate && !f.readOnly && !['status_id', 'assigned_tech_id', 'summary', 'details'].includes(f.name));
    return orderTicketFields(applyFieldConfig(base, fieldConfigOf(type, 'agent_detail'), { mode: 'edit' }));
  }, [meta.data, type]);
  // Long-text fields (notes, descriptions, rich text) crowd the inspector: two or more get their own tab.
  const { detailFields, moreFields } = useMemo(() => splitBlockFields(allDetailFields), [allDetailFields]);

  if (ticketQ.error) return <ErrorBanner error={ticketQ.error} action={<Button size="sm" variant="ghost" onClick={() => navigate('/tickets')}>Back</Button>} />;
  if (!ticket || !meta.data) return <div className="hf-page"><Skeleton height={28} width={240} /><div style={{ marginTop: 16 }}><Skeleton lines={8} /></div></div>;

  const category = st?.category;
  const closedish = category === 'resolved' || category === 'closed' || category === 'cancelled';
  const merged = Boolean(ticket['merged_into_ticket_id']);

  return (
    <div className="hf-page">
      <PageHeader
        compact
        breadcrumb={[{ label: 'Tickets', to: '/tickets' }, { label: String(ticket['ref'] ?? '') }]}
        eyebrow={<span className="hf-mono">{String(ticket['ref'] ?? '')}</span>}
        title={String(ticket['summary'] ?? '(no summary)')}
        badge={st ? <Badge tone={statusTone(st.category)}>{st.name}</Badge> : undefined}
        actions={
          <div className="hf-row" style={{ flexWrap: 'wrap' }}>
            <Field label="Status" id="sd-status">
              <Select id="sd-status" size="sm" value={String(ticket['status_id'] ?? '')} disabled={merged || status.isPending}
                onChange={(e) => changeStatus(e.target.value)}
                options={statuses.rows.filter((s) => s.is_active).map((s) => ({ value: s.id, label: s.name }))} />
            </Field>
            <Field label="Assignee" id="sd-assignee">
              <Combobox id="sd-assignee" size="sm" clearable placeholder="Unassigned" value={(ticket['assigned_tech_id'] as string | null) ?? null}
                options={techs.rows.map((t) => ({ value: t.id, label: t.name }))}
                onChange={(v) => changeAssignee(v)} aria-label="Assignee" />
            </Field>
            {!merged && !closedish && <TimerControl ticketId={id} onLogged={refreshAll} />}
            <Button size="sm" variant="ghost" icon="eye" onClick={() => watch.mutate({ id, body: {} }, { onSuccess: refreshAll })}>Watch</Button>
            {!closedish && <Button size="sm" variant="outline" icon="arrow-up" onClick={() => escalate.mutate({ id, body: {} }, { onSuccess: refreshAll })}>Escalate</Button>}
            {closedish && !merged && <Button size="sm" variant="primary" icon="rotate-ccw" onClick={() => reopen.mutate({ id, body: {} }, { onSuccess: refreshAll })}>Reopen</Button>}
            {!merged && <IconButton icon="git-branch" label="Merge into another ticket" size="sm" onClick={() => setMergeOpen(true)} />}
            <IconButton icon="panel-left" label={railOpen ? 'Hide the working set (r)' : 'Show the working set (r)'} size="sm" onClick={() => setRailOpen((v) => !v)} />
            <IconButton icon="panel-right" label={inspectorOpen ? 'Hide the inspector (i)' : 'Show the inspector (i)'} size="sm" onClick={() => setInspectorOpen((v) => !v)} />
          </div>
        }
      >
        <div className="hf-chiprow" aria-label="Ticket facts">
          {type && <Chip icon={(type.icon as IconName | null) ?? 'tag'}>{type.name}</Chip>}
          {pr && <Badge tone={priorityTone(pr.level)}>{pr.name}</Badge>}
          {queue && <Chip icon="inbox">{queue.name}</Chip>}
          {(sla.data?.data ?? []).length > 0 ? <SlaChips clocks={sla.data!.data} /> : dueChips(ticket).map((c) => <Badge key={c.key} tone={c.tone} title={c.title}>{c.label}</Badge>)}
          {merged && <Badge tone="neg">Merged</Badge>}
        </div>
      </PageHeader>

      <SplitPane
        sized="first"
        initial={264}
        min={200}
        max={420}
        collapse={railOpen ? undefined : 'first'}
        storageKey="hf.sd.workspace.rail"
        first={<QueueRail set={workingSet} currentId={id} mode={mode} onModeChange={changeMode} onOpen={openTicket} />}
        second={
      <SplitPane
        sized="second"
        initial={440}
        min={360}
        collapse={inspectorOpen ? undefined : 'second'}
        storageKey="hf.sd.ticket.inspector"
        first={
          <div className="hf-stack">
            {ticket['details'] ? <Description html={String(ticket['details'])} /> : null}
            {!merged && (
              <Composer
                ticketId={id}
                outcomes={outcomes.rows}
                statuses={statuses.rows}
                onDone={({ next }) => {
                  refreshAll();
                  if (next) openTicket(nextId);
                }}
              />
            )}
            <div className="hf-between">
              <h2 className="hf-section-title" style={{ margin: 0, border: 0 }}>Conversation</h2>
              <div className="hf-chiprow" role="tablist" aria-label="Feed filter">
                {(['all', 'public', 'internal'] as const).map((f) => (
                  <Chip key={f} role="tab" aria-selected={feedFilter === f} active={feedFilter === f} onClick={() => setFeedFilter(f)}>{f[0]!.toUpperCase() + f.slice(1)}</Chip>
                ))}
              </div>
            </div>
            {timeline.isLoading ? <Skeleton lines={4} /> : timeline.error ? <ErrorBanner error={timeline.error} /> : <Feed entries={timeline.rows} filter={feedFilter} hasMore={timeline.hasMore} loadingMore={timeline.loadingMore} onMore={timeline.loadMore} />}
          </div>
        }
        second={
          <aside className="hf-stack" aria-label="Ticket inspector">
            <Tabs
              variant="pills"
              value={tab}
              onChange={setTab}
              aria-label="Inspector sections"
              tabs={[
                { key: 'details', label: 'Details', icon: 'file-text' },
                ...(moreFields.length ? [{ key: 'more', label: 'More info', icon: 'align-left' as IconName, count: moreFields.length }] : []),
                { key: 'related', label: 'Related', icon: 'link', count: (links.data?.data.length ?? 0) || undefined },
                { key: 'checklist', label: 'Checklist', icon: 'check-square', count: (checklist.data?.data.length ?? 0) || undefined },
                { key: 'approvals', label: 'Approvals', icon: 'shield-check', count: (approvals.data?.data.length ?? 0) || undefined },
                { key: 'assist', label: 'Copilot', icon: 'sparkles' },
                { key: 'schedule', label: 'Schedule', icon: 'calendar' },
                { key: 'money', label: 'Money', icon: 'banknote' },
                { key: 'sla', label: 'SLA', icon: 'timer' },
                { key: 'activity', label: 'Activity', icon: 'activity' },
              ]}
            />
            <div className="hf-tabpanel" role="tabpanel">
              {tab === 'details' && (
                <div className="hf-stack">
                  <AutoForm key={String(ticket['updated_at'])} fields={detailFields} mode="edit" initial={ticket} layout="stack" submitLabel="Save" hideActions={false}
                    onSubmit={async (values) => { await update.mutateAsync({ id, body: values }); refreshAll(); }} />
                  <RelatedCard resource="contacts" id={(ticket['contact_id'] as string | null) ?? null} fields={['name', 'email', 'phone', 'mobile', 'title']} title="Contact" icon="user" />
                  <RelatedCard resource="sites" id={(ticket['site_id'] as string | null) ?? null} fields={['name', 'city', 'timezone', 'phone']} title="Site" icon="map-pin" />
                  <RelatedCard resource="clients" id={(ticket['client_id'] as string | null) ?? null} fields={['name', 'kind', 'status', 'phone']} title="Client" icon="building" />
                </div>
              )}
              {tab === 'more' && moreFields.length > 0 && (
                <AutoForm key={`more:${String(ticket['updated_at'])}`} fields={moreFields} mode="edit" initial={ticket} layout="stack" submitLabel="Save" hideActions={false}
                  onSubmit={async (values) => { await update.mutateAsync({ id, body: values }); refreshAll(); }} />
              )}
              {tab === 'related' && (
                <Card title="Linked tickets" icon="link">
                  {(links.data?.data ?? []).length === 0 ? <span className="hf-muted">No links</span> : (
                    <ul className="hf-stack" style={{ gap: 'var(--s-1)', listStyle: 'none', padding: 0, margin: 0 }}>
                      {(links.data?.data ?? []).map((l) => (
                        <li key={String(l['id'])} className="hf-row">
                          <Badge tone="muted" dot={false}>{String(l['link_type']).replace('_', ' ')}</Badge>
                          <Button variant="link" size="sm" onClick={() => navigate(`/tickets/${String(l['other_id'])}`)}>{String(l['other_ref'] ?? '')} {String(l['other_summary'] ?? '')}</Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              )}
              {tab === 'checklist' && (
                <Card title="Checklist" icon="check-square">
                  {(checklist.data?.data ?? []).length === 0 ? <span className="hf-muted">No items — add a template from the ticket type.</span> : (
                    <ul className="hf-stack" style={{ gap: 'var(--s-1)', listStyle: 'none', padding: 0, margin: 0 }}>
                      {(checklist.data?.data ?? []).map((i) => (
                        <li key={String(i['id'])}>
                          <label className="hf-row">
                            <input type="checkbox" checked={Boolean(i['done_at'])} onChange={(e) => toggle.mutate({ id, body: { item_id: i['id'], done: e.target.checked } }, { onSuccess: () => void checklist.refetch() })} />
                            <span className={i['done_at'] ? 'hf-muted' : ''}>{String(i['label'])}{i['is_required'] ? ' *' : ''}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              )}
              {tab === 'approvals' && (
                <Card title="Approvals" icon="shield-check">
                  {(approvals.data?.data ?? []).length === 0 ? <span className="hf-muted">No approvals</span> : (
                    <ul className="hf-stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {(approvals.data?.data ?? []).map((a) => (
                        <li key={String(a['id'])} className="hf-stack" style={{ gap: 'var(--s-1)' }}>
                          <div className="hf-row"><Badge tone={a['state'] === 'approved' ? 'pos' : a['state'] === 'rejected' ? 'neg' : a['state'] === 'pending' ? 'warn' : 'muted'}>{String(a['state'])}</Badge><span className="hf-muted hf-num">{formatDateTime(String(a['started_at']))}</span></div>
                          {a['state'] === 'pending' && (
                            <div className="hf-row">
                              <Button size="sm" variant="primary" icon="check" onClick={() => vote.mutate({ id: String(a['id']), body: { decision: 'approve' } }, { onSuccess: refreshAll })}>Approve</Button>
                              <Button size="sm" variant="danger" icon="x" onClick={() => vote.mutate({ id: String(a['id']), body: { decision: 'reject' } }, { onSuccess: refreshAll })}>Reject</Button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              )}
              {tab === 'assist' && <Card title="Copilot" icon="sparkles"><AssistPanel ticketId={id} onApplied={refreshAll} /></Card>}
              {tab === 'schedule' && <Card title="Book a time" icon="calendar"><SchedulePanel ticketId={id} onBooked={refreshAll} /></Card>}
              {tab === 'money' && <Card title="Commercials" icon="banknote"><CommercialsPanel ticketId={id} /></Card>}
              {tab === 'sla' && (
                <Card title="SLA clocks" icon="timer">
                  <dl className="hf-kv">
                    {(sla.data?.data ?? []).map((c) => (
                      <div key={c.id}><dt>{c.kind} #{c.cycle}</dt><dd><Badge tone={slaTone(c)}>{clockSummary(c)}</Badge> <span className="hf-muted hf-num">target {formatDateTime(c.target_at)}</span></dd></div>
                    ))}
                  </dl>
                  {(sla.data?.data ?? []).length === 0 && <span className="hf-muted">No SLA on this ticket type.</span>}
                </Card>
              )}
              {tab === 'activity' && <ActivityFeed entityType="ticket" entityId={id} />}
            </div>
          </aside>
        }
      />
        }
      />

      <Dialog open={mergeOpen} onClose={() => setMergeOpen(false)} title="Merge into another ticket" description="The feed is copied onto the target and this ticket is cancelled and frozen."
        footer={<><Button variant="ghost" onClick={() => setMergeOpen(false)}>Cancel</Button><Button variant="danger" busy={merge.isPending} disabled={!mergeInto} onClick={() => merge.mutate({ id, body: { into_ticket_id: mergeInto } }, { onSuccess: (r) => { setMergeOpen(false); navigate(`/tickets/${String((r as Row)['id'])}`); } })}>Merge</Button></>}>
        <Field label="Target ticket id" id="sd-merge-into"><Input id="sd-merge-into" value={mergeInto} onChange={(e) => setMergeInto(e.target.value)} placeholder="uuid" /></Field>
      </Dialog>
      <span className="hf-sr">Signed in as {principal.name ?? principal.email ?? 'you'}</span>
    </div>
  );
}
